<template>
  <div
    id="block-auth-user-wins"
    class="block-auth-user-wins"
    :class="{ 'is-on-grid': isOnGrid }"
  >
    <div class="block-auth-user-wins__inner">
      <AtomTextHeading
        v-if="title"
        html-tag="h2"
        :text="title"
        class="block-auth-user-wins__title"
        font-size="h2"
      />

      <BlockFilterTabs
        v-if="filterTabs?.length > 0"
        :tabs="filterTabs"
        class="block-auth-user-wins__tabs"
        :initial-active-tab="filterTabs[0].name"
        @on-tab-click="handleTabClick"
      />

      <BlockTableRows
        :rows="data"
        :is-loading="status !== 'success'"
        :no-results-text="currentTab?.noResultsText"
        :no-results-cta-text="currentTab?.noResultsCtaText"
        :no-results-cta-link="currentTab?.noResultsCtaLink"
        @on-redeem-prize="onRedeem($event)"
      />

      <transition name="fade-in">
        <BlockPaginationNumbers
          v-if="pages > 1"
          ref="paginationRef"
          class="block-auth-user-wins__pagination"
          :total-pages="pages"
          @on-page-change="handlePageChange"
        />
      </transition>
    </div>

    <BlockOverlayRedeem
      :is-open="isOverlayOpen"
      :title="overlayTitle"
      :text="overlayText"
      :submit-text="overlaySubmitText"
      :success-message="overlaySuccessMessage"
      :error-message="overlayErrorMessage"
      :form-fields="overlayFormFields"
      :redeem-body="redeemBody"
      @on-close="onCloseOverlay($event)"
    />
  </div>
</template>

<script setup>
const props = defineProps({
    isOnGrid: {
        type: Boolean,
        default: false,
    },

    /* Content */
    title: {
        type: String,
        default: '',
    },

    /*
        Settings
    */
    paginationSize: {
        type: Number,
        default: 5,
    },

    pages: {
        type: Number,
        default: 10,
    },

    filterTabs: {
        type: Array,
        default: () => [],
    },

    /*
        Storyblok Dummy Data
    */
    dummyData: {
        type: Array,
        default: null,
    },

    /*
        Redeem Overlay
    */
    overlayTitle: {
        type: String,
        default: '',
    },

    overlayText: {
        type: String,
        default: '',
    },

    overlaySubmitText: {
        type: String,
        default: '',
    },

    overlaySuccessMessage: {
        type: String,
        default: '',
    },

    overlayErrorMessage: {
        type: String,
        default: '',
    },

    overlayFormFields: {
        type: Array,
        default: () => [],
    },
});

/* Composables (must be called at top of setup) */
const { public: publicRuntimeConfig } = useRuntimeConfig();
const { locale } = useI18n();
const { data: modulesData } = useNuxtData('modules');

/* Module default content (reactive on locale change) */
const moduleLocaleContent = computed(() => {
    const modules = modulesData.value;
    const currentModule = modules?.yourWins;
    return currentModule?.content?.[locale.value] || null;
});
const columnsSettings = computed(() => moduleLocaleContent.value?.settings);
const receiptPlayedStrings = computed(() => moduleLocaleContent.value?.receiptPlayedStrings || {});

/* Data */
const data = ref(publicRuntimeConfig.IS_STORYBOOK ? props.dummyData : []);
const pages = ref(publicRuntimeConfig.IS_STORYBOOK ? props.pages : 0);
const currentPage = ref(1);
const onlyWins = ref(props.filterTabs[0]?.name === 'wins');

const { dayjs } = useDayJs();

/** Needed for POST /api/prizes/redeem — backend must expose one of these on each played row */
function resolveReceiptDocumentId(entry) {
    const prize = entry?.prize;
    const receipt = entry?.receipt && typeof entry.receipt === 'object' ? entry.receipt : null;
    const candidates = [
        entry?.receipt_document_id,
        entry?.receiptDocumentId,
        entry?.document_id,
        entry?.documentId,
        receipt?.receipt_document_id,
        receipt?.document_id,
        receipt?.documentId,
        prize?.receipt_document_id,
    ];
    const raw = candidates.find((v) => v != null && String(v).trim() !== '');
    return raw != null ? String(raw).trim() : null;
}

const fetchQuery = computed(() => {
    const q = {
        page: currentPage.value,
        pageSize: props.paginationSize,
        lang: locale.value,
    };
    if (onlyWins.value) {
        q.is_win = true;
    }
    return q;
});

const {
    pending,
    error,
    data: response,
    status,
    refresh,
} = useLazyFetch(
    '/api/receipts/played',
    {
        server: false,
        immediate: !publicRuntimeConfig.IS_STORYBOOK,
        query: fetchQuery,
        /*
            We watch the underlying primitives ourselves below.
            `useLazyFetch` would otherwise refetch on every new query object
            (e.g. props.paginationSize re-binding from the parent on locale
            change), causing the previous in-flight request to be aborted
            and leaving the data ref in an empty/inconsistent state.
        */
        watch: false,
        transform: (res) => {
            if (!res?.pagination) return;

            const { data: entries, pagination } = res;
            pages.value = Math.ceil(pagination.total / pagination.pageSize);
            data.value = [];

            const columnTemplate = columnsSettings.value?.receiptPlayedColumns;
            if (!columnTemplate?.length) {
                return;
            }

            const strings = receiptPlayedStrings.value;

            entries?.forEach((entry) => {
                const prize = entry.prize;
                const prizeName = prize?.name || strings.prizeFallback || '—';
                const dateFormatted = entry.date ? dayjs(entry.date).format('DD/MM/YYYY') : '';

                const redeemedRaw = prize?.redeemed_date
                    ?? entry.redeemed_date
                    ?? prize?.redeemed_at;

                const prizeIsWinStatus = String(prize?.status ?? '').toLowerCase() === 'win';
                const receiptDocumentId = resolveReceiptDocumentId(entry);

                const replaceObj = {
                    prize_name: prizeName,
                    date_formatted: dateFormatted,
                };

                const columnsToInsert = JSON.parse(JSON.stringify(columnTemplate));

                columnsToInsert.forEach((column) => {
                    const { content } = column;

                    if (column.name === 'status') {
                        content.buttonAction = undefined;
                        content.buttonText = undefined;
                        content.buttonValue = undefined;
                        content.subtitle = undefined;

                        if (
                            prizeIsWinStatus
                            && receiptDocumentId
                            && !redeemedRaw
                        ) {
                            content.buttonText = strings.redeemButton || '';
                            content.buttonAction = 'redeemPrize';
                            content.buttonValue = receiptDocumentId;
                        } else if (redeemedRaw && (entry.is_win || prizeIsWinStatus)) {
                            const redeemedFormatted = dayjs(redeemedRaw).format('DD/MM/YYYY');
                            content.subtitle = compileWithHandlebars(
                                strings.redeemedTemplate || 'Am {{date}} eingelöst',
                                { date: redeemedFormatted },
                                { noEscape: true },
                            );
                        } else if (!entry.is_win) {
                            content.subtitle = strings.statusAutoParticipation || '';
                        } else {
                            content.subtitle = strings.redeemUnavailable || '';
                        }
                        return;
                    }

                    Object.entries(content).forEach(([key, value]) => {
                        if (typeof content[key] === 'string') {
                            content[key] = compileWithHandlebars(value, replaceObj, { noEscape: true });
                        } else if (content[key] && typeof content[key] === 'object') {
                            const deepContent = content[key];
                            Object.entries(deepContent).forEach(([deepKey, deepValue]) => {
                                deepContent[deepKey] = compileWithHandlebars(deepValue, replaceObj, { noEscape: true });
                            });
                        }
                    });
                });

                data.value.push({
                    columns: columnsToInsert,
                });
            });
        },
    },
);

/*
    Manual refetch trigger. Vue batches multiple synchronous reactive
    changes (locale + props) into a single watcher invocation, so we
    only fire one request per locale/page/filter change.
*/
watch(
    () => [
        locale.value,
        currentPage.value,
        onlyWins.value,
        props.paginationSize,
    ],
    () => {
        if (publicRuntimeConfig.IS_STORYBOOK) return;
        refresh();
    },
);

watch(() => error.value, (e) => {
    if (e) {
        useSentryError(e, {
            useThrow: false,
            payload: {
                lang: locale.value,
                page: currentPage.value,
                pageSize: props.paginationSize,
                is_win: onlyWins.value ? true : undefined,
            },
            response: response?.value,
        });
    }
});

const currentTabIndex = ref(0);
const disabledSkeleton = ref(false);
const paginationRef = ref(null);

const handleTabClick = (event) => {
    disabledSkeleton.value = true;
    onlyWins.value = event === 'wins';
    currentTabIndex.value = props.filterTabs.findIndex((tab) => tab.name === event);
    currentPage.value = 1;
    paginationRef.value?.reset();
};

const handlePageChange = (page) => {
    currentPage.value = page;
    disabledSkeleton.value = true;
};

/*
    Redeem
*/
const isOverlayOpen = ref(false);
const redeemBody = ref({});

const onRedeem = async (receiptDocumentId) => {
    redeemBody.value = {
        receipt_document_id: receiptDocumentId,
    };
    isOverlayOpen.value = true;
};

const onCloseOverlay = (refetch = false) => {
    isOverlayOpen.value = false;

    if (refetch) {
        refresh();
    }
};

/*
    Currenttab
*/
const currentTab = computed(() => {
    if (!props.filterTabs?.length) return {};

    return props.filterTabs[currentTabIndex.value];
});
</script>

<style lang="scss" scoped>
.block-auth-user-wins {
    &.is-on-grid {
        @include wrapper-layout();
        @include grid-layout();

        margin-bottom: var(--m-bottom--medium);
    }
}

.block-auth-user-wins__inner {
    @include box-default;

    display: flex;
    flex-direction: column;
    row-gap: 20px;

    .is-on-grid & {
        @include default-content-columns('wide');
    }
}

.block-auth-user-wins__pagination {
    max-width: 500px;

    @include tablet-portrait {
        align-self: center;
    }
}

.block-auth-user-wins__tabs {
    @include fluid('--parent-padding', 38px, 32px, 60px, false);
    width: calc(100% + var(--parent-padding) * 2);
    margin-left: calc(var(--parent-padding) * -1);

    ::v-deep(.atom-tab) {
        &:first-child {
            margin-left: var(--parent-padding);
        }

        &:last-child {
            margin-right: var(--parent-padding);
        }
    }
}
</style>
